import { useState } from 'react';
import { Link } from 'react-router-dom';
import { mockDramas } from '../../../mocks/dramas';
import { createDramaUrl } from '../../../hooks/useNavigation';

const FeaturedAIDramas = () => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const featuredDramas = [...mockDramas]
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 4);

  const hero = featuredDramas[0];
  const rest = featuredDramas.slice(1, 4);

  return (
    <section className="rounded-2xl overflow-hidden">
      {/* Section Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-lg shadow-rose-500/20">
            <i className="ri-movie-2-line text-lg"></i>
          </div>
          <div>
            <h2 className="text-lg font-bold text-gray-900">Featured AI Dramas</h2>
            <p className="text-xs text-gray-500">Top-rated dramas loved by our community</p>
          </div>
        </div>
        <Link
          to="/dramas"
          className="text-sm font-medium text-rose-500 hover:text-rose-600 flex items-center gap-1 whitespace-nowrap cursor-pointer"
        >
          View All
          <i className="ri-arrow-right-s-line"></i>
        </Link>
      </div>

      {/* Hero + Side Stack Layout */}
      <div className="grid grid-cols-5 gap-4">
        {/* Hero Card - Left 3 cols */}
        <Link
          to={createDramaUrl(hero.genreSlug, hero.slug)}
          className="col-span-3 relative group rounded-2xl overflow-hidden cursor-pointer"
          onMouseEnter={() => setHoveredId(hero.id)}
          onMouseLeave={() => setHoveredId(null)}
        >
          <div className="w-full h-[420px]">
            <img
              src={hero.banner}
              alt={hero.title}
              className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
            />
          </div>
          {/* Cinematic overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-black/30 to-transparent"></div>

          {/* Top badges */}
          <div className="absolute top-4 left-4 flex items-center gap-2">
            <span className="px-3 py-1 bg-rose-500 text-white text-xs font-bold rounded-lg flex items-center gap-1.5 shadow-lg">
              <i className="ri-trophy-line"></i>
              #1 Featured
            </span>
          </div>

          {/* Content */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-2 mb-3">
              {hero.genre.map((g) => (
                <span
                  key={g}
                  className="px-2.5 py-1 bg-white/10 backdrop-blur-md text-white/90 text-xs font-medium rounded-lg border border-white/10"
                >
                  {g}
                </span>
              ))}
              <span className="px-2.5 py-1 bg-amber-500/20 backdrop-blur-md text-amber-300 text-xs font-bold rounded-lg border border-amber-400/20 flex items-center gap-1">
                <i className="ri-star-fill text-amber-400"></i>
                {hero.rating}
              </span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-2 tracking-tight">{hero.title}</h3>
            <p className="text-sm text-gray-300 line-clamp-2 max-w-md leading-relaxed">{hero.description}</p>
            <div className="flex items-center gap-5 mt-4 text-xs text-gray-400">
              <span className="flex items-center gap-1.5">
                <i className="ri-play-circle-line text-sm"></i>
                {hero.episodes} Episodes
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line text-sm"></i>
                {hero.views}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-map-pin-line text-sm"></i>
                {hero.country}
              </span>
            </div>

            {/* Watch button */}
            <div
              className={`mt-4 transition-all duration-300 ${
                hoveredId === hero.id ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
              }`}
            >
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 text-sm font-semibold rounded-xl hover:bg-gray-100 transition-colors whitespace-nowrap">
                <i className="ri-play-fill"></i>
                Watch Now
              </span>
            </div>
          </div>
        </Link>

        {/* Side Stack - Right 2 cols */}
        <div className="col-span-2 flex flex-col gap-4">
          {rest.map((drama, index) => (
            <Link
              key={drama.id}
              to={createDramaUrl(drama.genreSlug, drama.slug)}
              className="relative group rounded-2xl overflow-hidden flex-1 cursor-pointer"
              onMouseEnter={() => setHoveredId(drama.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              <div className="w-full h-full min-h-[128px]">
                <img
                  src={drama.banner}
                  alt={drama.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-110"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-black/10"></div>

              {/* Rank */}
              <div className="absolute top-3 left-3 w-8 h-8 flex items-center justify-center rounded-lg bg-white/90 text-gray-900 text-sm font-extrabold shadow-md">
                {index + 2}
              </div>

              {/* Rating */}
              <div className="absolute top-3 right-3 px-2 py-1 bg-black/40 backdrop-blur-md rounded-lg flex items-center gap-1 border border-white/10">
                <i className="ri-star-fill text-amber-400 text-xs"></i>
                <span className="text-white text-xs font-semibold">{drama.rating}</span>
              </div>

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="px-2 py-0.5 bg-white/10 backdrop-blur-sm text-white/80 text-[10px] font-medium rounded-md border border-white/10">
                    {drama.genre[0]}
                  </span>
                  <span className="text-[10px] text-gray-400">
                    {drama.episodes} Eps &middot; {drama.country}
                  </span>
                </div>
                <h4 className="text-sm font-bold text-white line-clamp-1">{drama.title}</h4>
              </div>

              {/* Hover play icon */}
              <div
                className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
                  hoveredId === drama.id ? 'opacity-100' : 'opacity-0'
                }`}
              >
                <div className="w-10 h-10 flex items-center justify-center bg-white/90 rounded-full shadow-xl">
                  <i className="ri-play-fill text-rose-500 text-lg ml-0.5"></i>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedAIDramas;
